import { z } from 'zod';
import {
  CadenceSchema,
  SectionIdSchema,
  SectionInsightsSchema,
} from './reportEnums.constants';

/** Per-section boilerplate stored on a template. */
export interface TemplateSectionConfigDto {
  calloutEnabled: boolean;
  narrative: string | null;
  callout: string | null;
  calloutTitle?: string | null;
  insights?: z.infer<typeof SectionInsightsSchema> | null;
  /** CSM-authored section body (Member Spotlight). Omitted for warehouse sections. */
  dataSnapshot?: Record<string, unknown> | null;
}

/** Per-section config object stored in template JSONB. */
export const TemplateSectionConfigSchema = z.object({
  calloutEnabled: z.boolean(),
  narrative: z.string().nullable(),
  callout: z.string().nullable(),
  calloutTitle: z.string().nullable().optional(),
  insights: SectionInsightsSchema.nullable().optional(),
  dataSnapshot: z.record(z.string(), z.unknown()).nullable().optional(),
});

/** Report template response DTO. */
export interface ReportTemplateDto {
  id: string;
  name: string;
  customerId: string | null;
  cadence: z.infer<typeof CadenceSchema>;
  sectionOrder: z.infer<typeof SectionIdSchema>[];
  sectionConfig: Partial<
    Record<z.infer<typeof SectionIdSchema>, TemplateSectionConfigDto>
  >;
  createdAt: string;
  updatedAt: string;
}

/** Request body for creating a template. */
export const CreateReportTemplateRequestSchema = z
  .object({
    name: z.string().min(1),
    customerId: z.string().uuid().nullable().optional(),
    cadence: CadenceSchema,
    sectionOrder: z.array(SectionIdSchema).default([]),
    sectionConfig: z
      .record(SectionIdSchema, TemplateSectionConfigSchema)
      .default({}),
  })
  .strict();

/** Parsed create-template request. */
export type CreateReportTemplateRequestDto = z.infer<
  typeof CreateReportTemplateRequestSchema
>;

/** Request body for updating a template. */
export const UpdateReportTemplateRequestSchema = z
  .object({
    name: z.string().min(1).optional(),
    customerId: z.string().uuid().nullable().optional(),
    cadence: CadenceSchema.optional(),
    sectionOrder: z.array(SectionIdSchema).optional(),
    sectionConfig: z
      .record(SectionIdSchema, TemplateSectionConfigSchema)
      .optional(),
  })
  .strict();

/** Parsed update-template request. */
export type UpdateReportTemplateRequestDto = z.infer<
  typeof UpdateReportTemplateRequestSchema
>;

/** Request body for creating a report from a template. */
export const CreateReportFromTemplateRequestSchema = z
  .object({
    customerId: z.string().uuid(),
    reportingPeriod: z.string().min(1),
  })
  .strict();

/** Parsed create-from-template request. */
export type CreateReportFromTemplateRequestDto = z.infer<
  typeof CreateReportFromTemplateRequestSchema
>;

/** Request body when saving a template from an existing report. */
export const CreateTemplateFromReportRequestSchema = z
  .object({
    name: z.string().min(1),
  })
  .strict();

/** Parsed save-template-from-report request. */
export type CreateTemplateFromReportRequestDto = z.infer<
  typeof CreateTemplateFromReportRequestSchema
>;
